import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../apiClient';
import type { LessonAccessLevel } from './lesson';

export interface LessonScheduleRule {
  id: number;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  type: string;
  accessLevel: LessonAccessLevel;
  professorId: number;
  academyId: number;
  maxSeats: number;
  active: boolean;
}

export interface LessonScheduleRulePayload {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  type: string;
  accessLevel?: LessonAccessLevel;
  professorId: number;
  academyId: number;
  maxSeats?: number;
  active?: boolean;
}

interface LessonScheduleRulesFilter {
  active?: boolean;
  academyId?: number;
}

function scheduleRulesQueryKey(filter?: LessonScheduleRulesFilter) {
  return ['lesson-schedule-rules', filter];
}

export function useGetLessonScheduleRules(filter?: LessonScheduleRulesFilter) {
  return useQuery(scheduleRulesQueryKey(filter), async () => {
    const params = new URLSearchParams();

    if (filter?.active !== undefined) {
      params.set('active', filter.active.toString());
    }

    if (filter?.academyId !== undefined) {
      params.set('academyId', filter.academyId.toString());
    }

    const { data } = await apiClient.get<LessonScheduleRule[]>(
      `/lesson-schedule-rules?${params.toString()}`,
    );
    return data;
  });
}

export function useCreateLessonScheduleRule() {
  const queryClient = useQueryClient();

  return useMutation(
    async (scheduleRule: LessonScheduleRulePayload) => {
      const { data } = await apiClient.post<LessonScheduleRule>(
        '/lesson-schedule-rules',
        scheduleRule,
      );
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['lesson-schedule-rules']);
      },
    },
  );
}

export function useUpdateLessonScheduleRule() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      id,
      changeset,
    }: {
      id: number;
      changeset: Partial<LessonScheduleRulePayload>;
    }) => {
      const { data } = await apiClient.patch<LessonScheduleRule>(
        `/lesson-schedule-rules/${id}`,
        changeset,
      );
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['lesson-schedule-rules']);
      },
    },
  );
}

export function useDeleteLessonScheduleRule() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id }: { id: number }) => {
      const { data } = await apiClient.delete<LessonScheduleRule>(`/lesson-schedule-rules/${id}`);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['lesson-schedule-rules']);
      },
    },
  );
}
